import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Button, Dropdown, Label, Typography } from '@heroui/react';
import { Icon } from '@iconify/react';
import { getNodeTestIdProp } from '@/utils';
import {
    LOCK_TILL_SHORTCUT,
    renderWithKeyShortcuts,
    ShortcutHint,
} from '@/components/shortcuts';

const renderLockItem = renderWithKeyShortcuts(
    'div',
    LOCK_TILL_SHORTCUT.combination,
);

type SellerIdentityMenuProps = {
    sellerName: string;
    sellerInitials: string;
    /** Locks the till (switch seller). */
    onLock: () => void;
};

/**
 * The signed-in seller's avatar and name, opening a menu whose one entry
 * locks the till; the `Ctrl+Shift+L` hint sits next to it and the item
 * names the key with `aria-keyshortcuts` (the shortcut itself is bound by
 * `SellerTopBar`).
 */
export const SellerIdentityMenu: React.FC<SellerIdentityMenuProps> = ({
    sellerName,
    sellerInitials,
    onLock,
}) => {
    const { t } = useTranslation('pages');

    return (
        <Dropdown>
            <Button
                aria-label={sellerName}
                className="gap-2 px-2"
                {...getNodeTestIdProp('seller-identity')}
                variant="ghost"
            >
                <Avatar size="sm">
                    <Avatar.Fallback>{sellerInitials}</Avatar.Fallback>
                </Avatar>
                <Typography
                    className="hidden sm:inline"
                    type="body-sm"
                    weight="medium"
                >
                    {sellerName}
                </Typography>
            </Button>
            <Dropdown.Popover>
                <Dropdown.Menu
                    aria-label={sellerName}
                    onAction={(key) => {
                        if (key === 'lock') {
                            onLock();
                        }
                    }}
                >
                    <Dropdown.Item
                        id="lock"
                        render={renderLockItem}
                        textValue={t('seller.lock')}
                        variant="danger"
                    >
                        <Icon
                            className="size-4"
                            icon="solar:lock-keyhole-bold-duotone"
                        />
                        <Label>{t('seller.lock')}</Label>
                        <ShortcutHint
                            className="ml-auto"
                            placement="control"
                            shortcut={LOCK_TILL_SHORTCUT}
                        />
                    </Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown.Popover>
        </Dropdown>
    );
};
